<template>
  <div class="documents-picker-table">
    <v-data-table-server
      v-model="internalSelection"
      :headers="headers"
      :row-props="getRowProps"
      :items="normalizedItems"
      :items-length="total"
      :items-per-page="pageSize"
      :loading="loading"
      item-value="selectableKey"
      show-select
      return-object
      hide-default-footer
      class="elevation-0 dp-picker-table"
    >
      <template v-slot:[`header.data-table-select`]>
        <v-checkbox-btn
          hide-details
          color="primary"
          :disabled="isSelectAllDisabled"
          :model-value="allSelectedFilesOnPage"
          :indeterminate="someSelectedFilesOnPage"
          @update:model-value="toggleSelectAllFilesOnPage"
        />
      </template>

      <template v-slot:[`item.data-table-select`]="{ item }">
        <AppTooltip
          v-if="!isSelectable(item)"
          location="top"
        >
          <template #activator="{ props }">
            <span v-bind="props" @click.stop>
              <v-checkbox-btn
                hide-details
                color="primary"
                :disabled="true"
                :model-value="false"
              />
            </span>
          </template>

          {{ $t('common.DocumentsPicker.folderNotSelectable') }}
        </AppTooltip>

        <AppTooltip
          v-else-if="isBlockedByCap(item)"
          location="top"
        >
          <template #activator="{ props }">
            <span v-bind="props" @click.stop>
              <v-checkbox-btn
                hide-details
                color="primary"
                :disabled="true"
                :model-value="false"
              />
            </span>
          </template>

          {{ $t('common.DocumentsPicker.maxSelectionReached', maxSelection, { count: maxSelection }) }}
        </AppTooltip>

        <v-checkbox-btn
          v-else
          hide-details
          color="primary"
          :model-value="isSelected(item)"
          @click.stop
          @update:model-value="toggleRow(item, $event)"
        />
      </template>

      <template v-slot:[`item.name`]="{ item }">
        <div class="picker-name-cell">
          <DocumentsTableItemFolderName
            v-if="item.type === 'folder'"
            :folder="item"
            :can-open-folder="false"
            @click-on-folder="$emit('open-folder', $event)"
          />

          <AppTooltip
            v-else-if="item.type === 'file' && isBlockedByCap(item)"
            location="top"
          >
            <template #activator="{ props }">
              <div v-bind="props">
                <DocumentsTableItemFileName
                  :file="item"
                  :can-open-file="false"
                />
              </div>
            </template>

            {{ $t('common.DocumentsPicker.maxSelectionReached', maxSelection, { count: maxSelection }) }}
          </AppTooltip>

          <DocumentsTableItemFileName
            v-else-if="item.type === 'file'"
            :file="item"
            :can-open-file="false"
          />
        </div>
      </template>
    </v-data-table-server>

    <div class="d-flex justify-end mt-4">
      <AppPagination
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        @update:current-page="$emit('update:page', $event)"
        @update:page-size="$emit('update:pageSize', $event)"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import DocumentsTableItemFileName from '@/project/documents/table/DocumentsTableItemFileName.vue'
import DocumentsTableItemFolderName from '@/project/documents/table/DocumentsTableItemFolderName.vue'

export default {
  name: 'DocumentsPickerTable',
  components: {
    AppTooltip,
    AppPagination,
    DocumentsTableItemFolderName,
    DocumentsTableItemFileName,
  },
  props: {
    items: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    total: { type: Number, required: true },
    page: { type: Number, required: true },
    pageSize: { type: Number, required: true },
    modelValue: { type: Array, default: () => [] },
    maxSelection: { type: Number, default: 0 },
  },
  emits: [
    'update:modelValue',
    'update:page',
    'update:pageSize',
    'open-folder',
  ],
  computed: {
    ...mapState('documents', ['numberingEnabled']),
    headers () {
      return [
        {
          title: this.$t('project.documents.table.DocumentsTableTemplate.name'),
          value: 'name',
        },
      ]
    },
    internalSelection: {
      get () {
        return this.modelValue || []
      },
      set (val) {
        const filesOnly = (val || []).filter(d => d && d.type === 'file')
        this.$emit('update:modelValue', filesOnly)
      },
    },
    normalizedItems () {
      return (this.items || []).map(i => ({
        ...i,
        selectableKey: `${i.type}-${i.id}`,
      }))
    },
    filesOnPage () {
      return (this.normalizedItems || []).filter(i => i?.type === 'file')
    },
    selectedKeysSet () {
      return new Set((this.internalSelection || []).map(d => `${d.type}-${d.id}`))
    },
    selectedFileKeysOnPage () {
      const set = this.selectedKeysSet
      return this.filesOnPage
        .map(f => f.selectableKey)
        .filter(k => set.has(k))
    },
    allSelectedFilesOnPage () {
      return this.filesOnPage.length > 0 &&
        this.selectedFileKeysOnPage.length === this.filesOnPage.length
    },
    someSelectedFilesOnPage () {
      return this.selectedFileKeysOnPage.length > 0 && !this.allSelectedFilesOnPage
    },
    isMaxReached () {
      return this.maxSelection > 0 && (this.internalSelection || []).length >= this.maxSelection
    },
    isSelectAllDisabled () {
      // "Select all" is fully disabled whenever a selection cap is active, to prevent exceeding it.
      return this.filesOnPage.length === 0 || this.maxSelection > 0
    },
  },
  methods: {
    getRowProps ({ item }) {
      return {
        class: {
          selected: this.isSelected(item),
          'picker-row--selectable': this.isSelectable(item) && !this.isBlockedByCap(item),
          'picker-row--disabled': this.isRowDisabled(item),
          'picker-row--cap-blocked': this.isBlockedByCap(item),
        },
        onClick: () => this.onRowClick(item),
      }
    },
    isSelectable (item) {
      return item?.type === 'file'
    },
    isSelected (item) {
      if (!item) return false
      return this.selectedKeysSet.has(item.selectableKey || `${item.type}-${item.id}`)
    },
    isBlockedByCap (item) {
      return this.isSelectable(item) && this.isMaxReached && !this.isSelected(item)
    },
    isRowDisabled (item) {
      return !this.isSelectable(item) || this.isBlockedByCap(item)
    },
    onRowClick (item) {
      if (this.isRowDisabled(item)) return
      this.toggleRow(item, !this.isSelected(item))
    },
    toggleRow (item, checked) {
      if (this.isRowDisabled(item)) return

      const key = item.selectableKey || `${item.type}-${item.id}`
      const current = this.internalSelection || []
      const set = new Set(current.map(d => `${d.type}-${d.id}`))

      if (checked) {
        if (set.has(key)) return
        this.internalSelection = [...current, item]
      } else {
        this.internalSelection = current.filter(d => `${d.type}-${d.id}` !== key)
      }
    },
    toggleSelectAllFilesOnPage () {
      const current = this.internalSelection || []
      const currentSet = new Set(current.map(d => `${d.type}-${d.id}`))
      const pageFileKeys = new Set(this.filesOnPage.map(f => f.selectableKey))

      if (this.allSelectedFilesOnPage) {
        // Unselect all files
        this.internalSelection = current.filter(
          d => !pageFileKeys.has(`${d.type}-${d.id}`),
        )
      } else {
        // Select missing files
        // Only reachable when there is no cap, "Select all" is disabled otherwise
        const toAdd = this.filesOnPage.filter(
          f => !currentSet.has(f.selectableKey),
        )
        this.internalSelection = [...current, ...toAdd]
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.documents-picker-table {
  :deep(.dp-picker-table) {
    font-size: 14px;
    border: get-border();
    @include border-radius('border-radius-4');
  }

  :deep(.dp-picker-table .v-table__wrapper > table) {
    table-layout: fixed;
    width: 100%;
  }

  :deep(.dp-picker-table .v-table__wrapper) {
    border-radius: inherit;
  }

  :deep(.dp-picker-table thead tr) {
    background: rgb(var(--v-theme-grey-50));
  }

  :deep(.dp-picker-table thead th) {
    font-weight: 600;
    color: rgb(var(--v-theme-grey-darken-3));
    height: 44px;
    padding: 10px 16px;
    border-bottom: get-border();
  }

  :deep(.dp-picker-table tbody td) {
    height: 52px;
    padding: 12px 16px;
    border-bottom: get-border();
    vertical-align: middle;
  }

  :deep(.dp-picker-table tbody tr:last-child td) {
    border-bottom: none;
  }

  :deep(.dp-picker-table tbody tr.picker-row--selectable) {
    cursor: pointer;
  }

  :deep(.dp-picker-table tbody tr.picker-row--disabled) {
    cursor: default;
  }

  :deep(.dp-picker-table tbody tr.picker-row--cap-blocked .picker-name-cell) {
    color: rgb(var(--v-theme-text-disabled));
  }

  :deep(.dp-picker-table tbody tr:hover td) {
    background: rgb(var(--v-theme-grey-50));
  }

  :deep(.dp-picker-table tbody tr.selected td) {
    background-color: rgb(var(--v-theme-surface-info));
  }

  :deep(.dp-picker-table th:first-child),
  :deep(.dp-picker-table td:first-child) {
    width: 44px;
    padding-left: 12px;
    padding-right: 8px;
  }

  :deep(.dp-picker-table .v-selection-control) {
    justify-content: center;
  }

  :deep(.picker-name-cell .filderName) {
    min-width: 50px;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
